// What we're working towards with Arlo — the household's five goals. Every
// screen that gives advice (Help now, Games, the weekly story, Ask) steers
// towards these; domain/goals.ts checks his logs against each one.

import type { BehaviourId } from './behaviour.ts';
import { cite } from './sources.ts';
import type { SourceRef } from './types.ts';

export type GoalId = 'manners' | 'quiet' | 'comfortable' | 'sociable' | 'calm';

export interface Goal {
  id: GoalId;
  title: string;
  /** What it looks like when it's working, as a grown dog. */
  aim: string;
  /** What gets him there, in puppyhood. */
  how: string[];
  /** Behaviours (Training → Help now) that work against it. */
  behaviours: BehaviourId[];
  /** Skills (content/training.ts) and games (content/games.ts) that build it. */
  skills: string[];
  games: string[];
  sources: SourceRef[];
}

export const GOALS: Goal[] = [
  {
    id: 'manners',
    title: 'Well-behaved',
    aim: 'Polite at home and out: four paws on the floor, swaps rather than steals, walks nicely and comes when called.',
    how: ['Short reward-based sessions, a few times a day — little and often beats long.', 'Reward what you want (sitting, four paws down, bringing things) far more than you react to what you don’t.', 'Everyone uses the same cue words and house rules.'],
    behaviours: ['bitey', 'jumping', 'stealing', 'chewing', 'lead-biting', 'humping'],
    skills: ['name-game', 'sit', 'recall', 'four-on-floor', 'swap', 'leave-it'],
    games: ['four-paws', 'swap-game', 'recall-ping-pong', 'tug-rules'],
    sources: cite('rewardBased', 'dtPuppyProblems', 'dtJumping'),
  },
  {
    id: 'quiet',
    title: 'No barking',
    aim: 'A dog who doesn’t need to bark: relaxed about noises, the door and being left, so barking stays rare and short.',
    how: ['Find what sets it off first (a log helps) — noise, the door, being left, frustration or excitement.', 'Reward quiet; don’t tell him off (it adds stress) and don’t give in to attention barking.', 'Get him used to flat noises — buzzer, lift, street, neighbours — early and gently.'],
    behaviours: ['barking', 'crate-crying'],
    skills: ['settle', 'alone-time'],
    games: ['scatter', 'lick-mat', 'crate-games'],
    sources: cite('dtBarking', 'soundTherapy', 'dtVisitors'),
  },
  {
    id: 'comfortable',
    title: 'Comfortable',
    aim: 'Happy in his own skin: sleeps well, likes his crate, is fine left for a while, and is easy to handle at the vet and groomer.',
    how: ['Plenty of sleep in a quiet spot — overtired puppies are unhappy puppies.', 'Build alone time up from seconds, so he learns you always come back.', 'Little and often gentle handling of paws, ears and teeth, with treats.'],
    behaviours: ['crate-crying', 'velcro', 'pen'],
    skills: ['crate-love', 'alone-time', 'handling'],
    games: ['crate-games', 'calm-handling', 'lick-mat'],
    sources: cite('rkcSleep', 'dtSeparation', 'rspcaAlone', 'battersea'),
  },
  {
    id: 'sociable',
    title: 'Sociable',
    aim: 'Friendly and relaxed with people, dogs and the city — confident, not over-excited or worried.',
    how: ['Lots of short, positive new experiences before about 16 weeks — watching counts.', 'Go at his pace: if he’s unsure, add distance and treats; never force a meeting.', 'Calm adult dogs that are vaccinated and friendly; carry him out before his jabs are done.'],
    behaviours: ['jumping'],
    skills: ['calm-greetings'],
    games: ['calm-handling'],
    sources: cite('dtSocialisation', 'dtOtherDogs', 'rkcSocial', 'pdsaSocial'),
  },
  {
    id: 'calm',
    title: 'Calm',
    aim: 'Can switch off: settles on his bed, recovers quickly after excitement and isn’t wound up by everyday life.',
    how: ['Protect naps — most wild spells are tiredness.', 'Teach a settle on a mat, and play games with pauses in them.', 'Sniffing, licking and chewing are calming: use them after excitement.'],
    behaviours: ['zoomies', 'wont-settle', 'bitey', 'velcro'],
    skills: ['settle', 'impulse-control'],
    games: ['off-switch', 'scatter', 'lick-mat'],
    sources: cite('batterseaSettle', 'dtHyper', 'rkcSleep'),
  },
];
